import { useState, useEffect } from "react";
import moment from "moment-timezone";
import { detectFVG } from "./detectFVG";
import { detectOrderBlock } from "../OrderBook/detectOrderBlock"; // Import the new function
import { detectLiquiditySweeps } from "../LiquiditySweep/detectLiquiditySweeps"


export const useChartData = (
  currencyPair,
  timeInterval,
  timeZone,
  gapThreshold,
  lsGapThreshold,
  setMaxDifference,
  setLSMaxDifference,
  cacheLevel,
  setCacheLevel,
  dateRange,
  setDateRange, // Accept dateRange and its setter
  availableDates,
  setAvailableDates,
  currentPrice,
  setCurrentPrice,
  lossRatio,
  gainRatio,
  orderBookImbalance, orderBookRange, orderBookCandleRange, showOrderBook,
  showFvgData,
  seriesId,
  showLiquiditySweep
) => {
  const [rawData, setRawData] = useState(null); // Store raw data
  const [chartData, setChartData] = useState(null); // Store chart data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  // Fetch raw data
  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch("https://vidplay-api.deng-software.com/fx", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ currencyPair, timeInterval }),
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const rawData = data.data;
        setRawData(rawData);
      } catch (err) {
        setError(`Failed to fetch chart data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [currencyPair, timeInterval, cacheLevel, setCacheLevel]);

  // Prepare chart data from raw data
  useEffect(() => {
    if (!rawData) return;
    try {
      // Process the raw data
      const processedData = rawData
        .map((point) => ({
          time: moment
            .tz(point.time, "UTC")
            .tz(timeZone)
            .format("YYYY-MM-DD HH:mm:ss"),
          open: point.open,
          high: point.high,
          low: point.low,
          close: point.close,
        }))
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      // Set the current price
      const latestDataPoint = processedData[processedData.length - 1];
      if (latestDataPoint) {
        setCurrentPrice(latestDataPoint.close);
      }

      // Set available dates based on selected timeZone
      if (processedData.length > 0) {
        setAvailableDates({
          start: moment
            .tz(processedData[0]?.time, "UTC")
            .tz(timeZone)
            .format("YYYY-MM-DDTHH:mm:ss"),
          end: moment
            .tz(processedData[processedData.length - 1]?.time, "UTC")
            .tz(timeZone)
            .format("YYYY-MM-DDTHH:mm:ss"),
        });
      }

      // Detect FVG
      const { bullishFVG, bearishFVG } = detectFVG(
        currencyPair,
        processedData,
        gapThreshold,
        lsGapThreshold,
        dateRange.start,
        currentPrice
      );

      // Detect Order Blocks
      const { bullishOrderBlocks, bearishOrderBlocks } = detectOrderBlock(
        currencyPair,
        processedData,
        dateRange.start,
        orderBookImbalance,
        orderBookRange,
        orderBookCandleRange
      );

      const { liquiditySweeps } = detectLiquiditySweeps(currencyPair, processedData, lsGapThreshold, dateRange);

      // If date range is not set, cover the full processed data
      if (!dateRange.start || !dateRange.end) {
        setDateRange({
          start: processedData[0]?.time,
          end: processedData[processedData.length - 1]?.time,
        });
      }

      // Calculate maximum gap difference
      const maxBullishGap = Math.max(
        ...bullishFVG.map((gap) => Math.abs(gap.gap)),
        0
      );
      const maxBearishGap = Math.max(
        ...bearishFVG.map((gap) => Math.abs(gap.gap)),
        0
      );
      const maxGap = Math.max(maxBullishGap, maxBearishGap);
      setMaxDifference(maxGap);

      // Chart traces
      const traceCandlesticks = {
        x: processedData.map((point) => point.time),
        open: processedData.map((point) => point.open),
        high: processedData.map((point) => point.high),
        low: processedData.map((point) => point.low),
        close: processedData.map((point) => point.close),
        type: "candlestick",
        name: `${currencyPair} Candlesticks`,
        showlegend: false,
      };

      // Prepare FVG Traces
      let traceBullishFVG = null;
      let traceBearishFVG = null;
      let bullishFVGLines = [];
      let bearishFVGLines = [];
      let markerToLinesMap = [];


      // Prepare Order Block Traces
      let traceBullishOrderBlocks = [];
      let traceBearishOrderBlocks = [];

      // Prepare Liquidity Traces
      let traceBullishSweeps = [];
      let traceBearishSweeps = [];

      if (showFvgData) {

        traceBullishFVG = {
          x: bullishFVG.map((gap) => gap.time),
          y: bullishFVG.map((gap) => gap.upper),
          mode: "markers+text",
          marker: { color: "green", size: 14, symbol: "triangle-up" },
          text: bullishFVG.map((gap) => `<b>${gap.gap.toFixed(6)}</b>`),
          textposition: "top center",
          textfont: {
            size: 14,
            color: "green",
          },
          name: `Bullish FVG (${bullishFVG.length})`,
          hovertext: bullishFVG.map((gap) => {
            // (You could do the above logic inside this map, using gap.upperBound if needed)
            const totalDistance = gap.gap;
            // const ratioPlusOne = rrRatio + 1;

            let targetPrice = currentPrice + (gap.gap * gainRatio);
            // let onePart = totalDistance / lossRatio;
            let stopLoss = currentPrice - (gap.gap * lossRatio);

            if (stopLoss < 0) stopLoss = 0;
            // 5) Potential gain in PRICE is (targetPrice - currentPrice)
            const potentialGain = gap.gap * gainRatio; // e.g., ~16.67

            // 6) Convert that price distance to TICKS
            const potentialGainTicks = potentialGain / gap.tickSize; // e.g. 1667 ticks if tickSize=0.01

            // 7) Convert TICKS to USD
            const potentialGainUSD = potentialGainTicks * gap.tickValue;  // e.g. 1667*$10= $16,670

            const potentialLoss = gap.gap * lossRatio; // 2

            // Convert to TICKS
            const potentialLossTicks = potentialLoss / gap.tickSize; // 200

            // Convert to USD
            const potentialLossUSD = potentialLossTicks * gap.tickValue; // 2000

            return `BULLISH FVG<br>Time: ${moment(gap.time).format("HH:mm:ss")}<br>Data: ${moment(gap.time).format(
              "YYYY-MM-DD"
            )}<br>Potential FVG Gain: $${gap.potentialGain}
      <br>FVG Gap: ${gap.gap}
        <br>Potential Current Price Gain: $${gap.potentialGainFromCurrent}
        <br>RISK TO REWARD CALCULATION (R:R)
        <br>  Current Price: $${currentPrice}
        <br>  gainPriceDistance: ${potentialGain.toFixed(6)}
        <br>  gainTicks: ${potentialGainTicks.toFixed(0)}
        <br>  lossPriceDistance: ${potentialLoss.toFixed(6)}
        <br>  lossTicks: ${potentialLossTicks.toFixed(0)}
        <br>  R:R Ratio: ${lossRatio}:${gainRatio}
        <br>  Target Price: $${targetPrice.toFixed(6)}
        <br>  Stop Loss: $${stopLoss.toFixed(6)}
        <br>  R:R Potential Gain: $${potentialGainUSD.toFixed(2)}
        <br>  R:R Potential Loss: $${potentialLossUSD.toFixed(2)}
        <br>PRICE PARAMETERS
        <br>  TickValue: $${gap.tickValue}
        <br>  TickSize: $${gap.tickSize}
      `;
          }),
          /*  hovertext: bullishFVG.map(
             (gap) =>
               `BULLISH FVG<br>Potential Gain: $${gap.potentialGain}
              <br>Potential Gain (from current): $${gap.potentialGainFromCurrent}
             <br>Upper Price:  ${gap.upper}
             <br>Lower Price:  ${gap.lower}
             <br>Gap Value:  ${gap.gap}`
           ), */
          hoverinfo: "text",
          hoverlabel: {
            font: {
              size: 16,
              color: "white",
              weight: "bold",
            },
          },
        };

        traceBearishFVG = {
          x: bearishFVG.map((gap) => gap.time),
          y: bearishFVG.map((gap) => gap.lower),
          mode: "markers+text",
          marker: { color: "red", size: 16, symbol: "triangle-down" },
          text: bearishFVG.map((gap) => `<b>${gap.gap.toFixed(6)}</b>`),
          textposition: "bottom center",
          textfont: {
            size: 14,
            color: "red",
          },
          name: `Bearish FVG (${bearishFVG.length})`,
          hovertext: bearishFVG.map((gap) => {

            let targetPrice = currentPrice - (gap.gap * gainRatio);
            // let onePart = totalDistance / lossRatio;
            let stopLoss = currentPrice + (gap.gap * lossRatio);

            if (targetPrice < 0) targetPrice = 0;
            // 5) Potential gain in PRICE is (targetPrice - currentPrice)
            const potentialGain = gap.gap * gainRatio; // e.g., ~16.67

            // 6) Convert that price distance to TICKS
            const potentialGainTicks = potentialGain / gap.tickSize; // e.g. 1667 ticks if tickSize=0.01

            // 7) Convert TICKS to USD
            const potentialGainUSD = potentialGainTicks * gap.tickValue;  // e.g. 1667*$10= $16,670

            const potentialLoss = gap.gap * lossRatio; // 2

            // Convert to TICKS
            const potentialLossTicks = potentialLoss / gap.tickSize; // 200

            // Convert to USD
            const potentialLossUSD = potentialLossTicks * gap.tickValue; // 2000
            return `BEARISH FVG<br>Time: ${moment(gap.time).format("HH:mm:ss")}<br>Data: ${moment(gap.time).format(
              "YYYY-MM-DD"
            )}<br>Potential FVG Gain: $${gap.potentialGain}
      <br>FVG Gap: ${gap.gap}
        <br>Potential Current Price Gain: $${gap.potentialGainFromCurrent}
        <br>RISK TO REWARD CALCULATION (R:R)
        <br>  Current Price: $${currentPrice}
        <br>  gainPriceDistance: ${potentialGain.toFixed(6)}
        <br>  gainTicks: ${potentialGainTicks.toFixed(0)}
        <br>  lossPriceDistance: ${potentialLoss.toFixed(6)}
        <br>  lossTicks: ${potentialLossTicks.toFixed(0)}
        <br>  R:R Ratio: ${lossRatio}:${gainRatio}
        <br>  Target Price: $${targetPrice.toFixed(6)}
        <br>  Stop Loss: $${stopLoss.toFixed(6)}
        <br>  R:R Potential Gain: $${potentialGainUSD.toFixed(2)}
        <br>  R:R Potential Loss: $${potentialLossUSD.toFixed(2)}
        <br>PRICE PARAMETERS
        <br>  TickValue: $${gap.tickValue}
        <br>  TickSize: $${gap.tickSize}`;
          }),
          hoverinfo: "text",
          hoverlabel: {
            font: {
              size: 16,
              color: "white",
              weight: "bold",
            },
          },
        };
        /**
         * Determine if the bullish FVG has been "broken" AFTER it was established.
         * We'll define "broken" as:
         *   - There is a closing price (currentPrice) that is outside (above the upper or below the lower)
         *   - The last candle's timestamp is strictly after gap.firstCandleTime
         */
        function isBullishBroken(gap, processedData) {
          // If we don't have the index in the gap object, we can find it
          // from the time. But let's assume we have gap.thirdCandleIndex.
          const thirdCandleIndex = gap.thirdCandleIndex;

          // Start checking from the candle AFTER the third candle
          for (let i = thirdCandleIndex + 1; i < processedData.length; i++) {
            const candle = processedData[i];

            // EXAMPLE condition:
            // If the entire candle is strictly above the gap's upper 
            // or strictly below the gap's lower, we consider it "broken."
            // You might prefer checking the close or any other logic:
            if (candle.high >= gap.upper) {
              return true;
            }
          }

          // If no candle after the third candle is fully out of the gap,
          // then we say it's not broken
          return false;
        }
        // Create horizontal lines for bullish FVG, using dashed style if “broken”
        bullishFVGLines = bullishFVG.flatMap((gap, index) => {
          const broken = isBullishBroken(gap, processedData);
          return [
            {
              x: [gap.firstCandleTime, processedData[processedData.length - 1]?.time],
              y: [gap.upper, gap.upper],
              mode: "lines",
              line: {
                color: "green",
                width: broken ? 1 : 3,
                dash: broken ? "dash" : "solid",
              },
              name: `Bullish FVG Upper #${index + 1}`,
              showlegend: false,
            },
            {
              x: [gap.firstCandleTime, processedData[processedData.length - 1]?.time],
              y: [gap.lower, gap.lower],
              mode: "lines",
              line: {
                color: "green",
                width: broken ? 1 : 3,
                dash: broken ? "dash" : "solid",
              },
              name: `Bullish FVG Lower #${index + 1}`,
              showlegend: false,
            },
          ];
        });
        function isBearishBroken(gap, processedData) {
          // If we don't have the index in the gap object, we can find it
          // from the time. But let's assume we have gap.thirdCandleIndex.
          const thirdCandleIndex = gap.thirdCandleIndex;
          // Start checking from the candle AFTER the third candle
          for (let i = thirdCandleIndex + 1; i < processedData.length; i++) {
            const candle = processedData[i];

            // EXAMPLE condition:
            // If the entire candle is strictly above the gap's upper 
            // or strictly below the gap's lower, we consider it "broken."
            // You might prefer checking the close or any other logic:
            if (candle.low <= gap.lower) {
              return true;
            }
          }

          // If no candle after the third candle is fully out of the gap,
          // then we say it's not broken
          return false;
        }
        // Create horizontal lines for bullish FVG, using dashed style if “broken”
        bearishFVGLines = bearishFVG.flatMap((gap, index) => {
          const broken = isBearishBroken(gap, processedData);
          return [
            {
              x: [gap.firstCandleTime, processedData[processedData.length - 1]?.time],
              y: [gap.upper, gap.upper],
              mode: "lines",
              line: {
                color: "red",
                width: broken ? 1 : 3,
                dash: broken ? "dash" : "solid",
              },
              name: `Bearish FVG Upper #${index + 1}`,
              showlegend: false,
            },
            {
              x: [gap.firstCandleTime, processedData[processedData.length - 1]?.time],
              y: [gap.lower, gap.lower],
              mode: "lines",
              line: {
                color: "red",
                width: broken ? 1 : 3,
                dash: broken ? "dash" : "solid",
              },
              name: `Bearish FVG Lower #${index + 1}`,
              showlegend: false,
            },
          ];
        });
        // Build markerToLinesMap for legend toggling
        //  - The bullish lines start at index=3 (since we already used 0,1,2)
        //  - Each bullish FVG has 2 lines => total lines = bullishFVG.length * 2
        const bullishLineCount = bullishFVG.length * 2;
        const bullishLineStart = 3;
        const bullishLineIndices = Array.from(
          { length: bullishLineCount },
          (_, i) => bullishLineStart + i
        );

        // The Bearish lines come after all bullish lines
        const bearishLineStart = bullishLineStart + bullishLineCount;
        const bearishLineCount = bearishFVG.length * 2;
        const bearishLineIndices = Array.from(
          { length: bearishLineCount },
          (_, i) => bearishLineStart + i
        );

        // Index 1 => bullish marker => lines in bullishLineIndices
        // Index 2 => bearish marker => lines in bearishLineIndices
        markerToLinesMap = {
          1: bullishLineIndices,
          2: bearishLineIndices,
        };

      }

      if (showOrderBook) {

        // Order Block Traces
        traceBullishOrderBlocks = bullishOrderBlocks.map((block, index) => ({
          x: [block.time, processedData[processedData.length - 1]?.time],
          y: [block.close, block.close],
          mode: "lines",
          line: {
            color: "blue",
            width: 2,
            dash: "solid",
          },
          name: `Bullish Order Block ${index + 1}`,
          hoverinfo: "text",
          /* hovertext: `
            BULLISH ORDER BLOCK
            <br>Stop Loss: $${block.stopLoss.toFixed(2)}
            <br>Target Price: $${block.targetPrice.toFixed(2)}
            <br>R/R Ratio: 1:${gainRatio}
            <br>Potential Gain (USD): $${block.potentialGainUSD.toFixed(2)}
            <br>Potential Loss (USD): $${block.potentialLossUSD.toFixed(2)}
            <br>Price Range: $${block.low} - $${block.high}
          `, */
          hoverlabel: {
            font: {
              size: 16,
              color: "white",
              weight: "bold",
            },
          },
        }));

        traceBearishOrderBlocks = bearishOrderBlocks.map((block, index) => ({
          x: [block.time, processedData[processedData.length - 1]?.time],
          y: [block.open, block.open],
          mode: "lines",
          line: {
            color: "orange",
            width: 2,
            dash: "solid",
          },
          name: `Bearish Order Block ${index + 1}`,
          hoverinfo: "text",
          /* hovertext: `
            BEARISH ORDER BLOCK
            <br>Stop Loss: $${block.stopLoss.toFixed(2)}
            <br>Target Price: $${block.targetPrice.toFixed(2)}
            <br>R/R Ratio: 1:${gainRatio}
            <br>Potential Gain (USD): $${block.potentialGainUSD.toFixed(2)}
            <br>Potential Loss (USD): $${block.potentialLossUSD.toFixed(2)}
            <br>Price Range: $${block.low} - $${block.high}
          `, */
          hoverlabel: {
            font: {
              size: 16,
              color: "white",
              weight: "bold",
            },
          },
        }));


      }

      if (showLiquiditySweep) {
        // Calculate the maximum sweep difference
        const maxDifference = liquiditySweeps.reduce(
          (max, sweep) => Math.max(max, Math.abs(sweep.difference)),
          0
        );
        setLSMaxDifference(maxDifference);

        // Add breach percentage calculation
        liquiditySweeps.forEach((sweep) => {
          sweep.breachPercentage = ((sweep.reversalClose - sweep.breachLevel) / sweep.breachLevel) * 100;
        });

        // Separate bullish and bearish sweeps
        const bullishSweeps = liquiditySweeps.filter((sweep) => sweep.direction === "bullish");
        const bearishSweeps = liquiditySweeps.filter((sweep) => sweep.direction === "bearish");

        traceBullishSweeps = {
          x: bullishSweeps.map((sweep) => sweep.time),
          y: bullishSweeps.map((sweep) => sweep.breachLevel),
          mode: "markers+text",
          marker: { color: "green", size: 12, symbol: "diamond" },
          text: bullishSweeps.map((sweep) => `<b>${sweep.difference}</b>`),
          textposition: "top center",
          textfont: {
            size: 14,
            color: "green",
          },
          name: `Bullish Sweeps (${bullishSweeps.length})`,
          hovertext: bullishSweeps.map(
            (sweep) =>
              `BULLISH SWEEP<br>Time: ${moment(sweep.time).format("HH:mm:ss")}<br>Data: ${moment(sweep.time).format(
                "YYYY-MM-DD"
              )}<br>Breach Level: ${sweep.breachLevel}<br>Reversal Close: ${sweep.reversalClose}<br>Difference: ${sweep.difference
              }<br>Breach %: ${sweep.breachPercentage.toFixed(2)}%`
          ),
          hoverinfo: "text",
          hoverlabel: {
            font: {
              size: 16,
              color: "white",
              weight: "bold",
            },
          },
        };

        traceBearishSweeps = {
          x: bearishSweeps.map((sweep) => sweep.time),
          y: bearishSweeps.map((sweep) => sweep.breachLevel),
          mode: "markers+text",
          marker: { color: "red", size: 12, symbol: "diamond" },
          text: bearishSweeps.map((sweep) => `<b>${sweep.difference}</b>`),
          textposition: "top center",
          textfont: {
            size: 14,
            color: "red",
          },
          name: `Bearish Sweeps (${bearishSweeps.length})`,
          hovertext: bearishSweeps.map(
            (sweep) =>
              `BEARISH SWEEP<br>Time: ${moment(sweep.time).format("HH:mm:ss")}<br>Data: ${moment(sweep.time).format(
                "YYYY-MM-DD"
              )}<br>Breach Level: ${sweep.breachLevel}<br>Reversal Close: ${sweep.reversalClose}<br>Difference: ${sweep.difference
              }<br>Breach %: ${sweep.breachPercentage.toFixed(2)}%`
          ),
          hoverinfo: "text",
          hoverlabel: {
            font: {
              size: 16,
              color: "white",
              weight: "bold",
            },
          },
        };
      }

      // Combine all traces
      const traces = [
        traceCandlesticks,   // index 0
        traceBullishFVG,     // index 1
        traceBearishFVG,     // index 2
        traceBullishSweeps,
        traceBearishSweeps,
        ...bullishFVGLines,  // subsequent
        ...bearishFVGLines,  // subsequent
        ...traceBullishOrderBlocks, // subsequent
        ...traceBearishOrderBlocks, // subsequent
      ];


      const layout = {
        xaxis: {
          title: `Time (${timeZone})`,
          type: "date",
          tickformat: "%b %d %H:%M",
          range: [dateRange.start, dateRange.end],
        },
        yaxis: {
          title: "Price",
          autorange: true,
        },
        hovermode: "closest",
      };

      setChartData({
        traces,
        layout,
        markerToLinesMap, // <-- so the chart component can toggle lines
      });
    } catch (err) {
      setError(`Failed to process chart data: ${err.message}`);
    }
  }, [
    rawData,
    currencyPair,
    timeZone,
    gapThreshold,
    lsGapThreshold,
    setMaxDifference,
    dateRange,
    currentPrice,
    lossRatio,
    gainRatio,
    orderBookImbalance, orderBookRange, orderBookCandleRange, showOrderBook, showFvgData, showLiquiditySweep
  ]);

  return { chartData, loading, error, availableDates };
};
